/**
 * CLH Band — the real price at which the NEXT CLH body breaks out of this one.
 *
 * WHY THIS EXISTS
 *
 * CLH candles are a heavily smoothed transform, so their levels are not tradeable
 * prices. This inverts them: given the candles so far, it solves for the REAL
 * price the next bar would have to close at for the next CLH body to clear the
 * current body's top (a long breakout / short cut-off), to break its bottom (the
 * mirror), or simply to change colour.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE SOLVE IS EXACT
 *
 * Every step of CLH is linear — EMA, linreg, and averaging — and the min/max
 * calls in `transformCLH` only shape the drawn HIGH and LOW, never the close. So
 *
 *     CLH.close[i+1]  =  B(i)  +  dO·o  +  dH·h  +  dL·l  +  dC·c
 *
 * with dO/dH/dL/dC constant once the EMA and regression warm-ups are past, and
 * B(i) carrying the whole recursion state at bar i. Measured affinity error over
 * real history: 5.6e-14% of price. This is a closed form, not an approximation.
 *
 * Two further facts make it usable, both verified numerically rather than assumed:
 *
 *   - `CLH.open[i+1]` does NOT depend on bar i+1 at all. It comes from
 *     (haOpen[i] + haClose[i])/2 and (slrHaOpen[i] + slrHaClose[i])/2, both fixed
 *     at bar i's close. That is what makes the flip level solvable.
 *   - dH === dL and dO === dC, by symmetry of the (o+h+l+c)/4 and
 *     (slrO+slrH+slrL+slrC)/4 steps. Asserted at calibration; if it ever stops
 *     holding, the caller is told rather than silently given a wrong level.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW IT AVOIDS RE-IMPLEMENTING THE CANDLE MATHS
 *
 * The obvious approach — rebuild the CSH/CSL chains here to get their internal
 * state — would put a second copy of the candle arithmetic in the repo, free to
 * drift from `candleTransforms.ts`. It is also unnecessary.
 *
 * B(i) can be recovered from the REAL series instead. Bar i+1 actually happened,
 * so `transformCandles(candles,'CLH')[i+1].close` already equals
 * B(i) + dO·o + dH·h + dL·l + dC·c for that bar's real OHLC — rearrange for B(i).
 * The coefficients are measured once at runtime by perturbing a probe bar through
 * the SHIPPED transform. So this file computes the whole series from ONE call to
 * `transformCandles` plus five probe calls, and contains no candle maths of its
 * own. If `candleTransforms.ts` changes, this follows it automatically.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CAUSALITY
 *
 * The level at index i is computed from bars 0..i ONLY and is the level that
 * governs bar i+1. It becomes known at bar i's CLOSE. The caller must therefore
 * draw point i starting at bar i+1 — drawing it on bar i would imply it was
 * available while bar i was still forming. `verifyClhBand.ts` proves the absence
 * of look-ahead by prefix-invariance: recomputing on candles[0..i] must give the
 * same value at i as computing on the full array.
 *
 * THE NEXT BAR IS MODELLED AS "PRICE TRAVELS THERE": open = close[i],
 * high = max(open, X), low = min(open, X), close = X. Chosen deliberately over a
 * flat candle (which would put the levels at roughly half the distance but assume
 * a bar with no range) and over using the live bar's own high/low (which would
 * make the level move intrabar, so it could not carry a resting order and the
 * history would not match what you would have seen live).
 */
import { CandleRow } from '../components/charts/TradingChart';
import { transformCandles } from './candleTransforms';

export interface ClhLevelPoint {
  /** The bar whose CLOSE produced these levels. They govern the NEXT bar. */
  time: number;
  /** Price at which the next CLH body would close ABOVE this body's top. */
  upper: number | null;
  /** Price at which the next CLH body would close BELOW this body's bottom. */
  lower: number | null;
  /** Price at which the next CLH body would change COLOUR. */
  flip: number | null;
  /** This bar's CLH body direction — the trend the body is showing. */
  bodyUp: boolean;
}

export interface ClhBandOptions {
  /** Bars to skip before emitting anything. The EMA and linreg warm-ups use a
   *  cumulative mean, so the coefficients are not yet their steady-state values
   *  and the affine model does not hold. Default 30 (EMA10 ∘ EMA3 with a
   *  linreg-14 alongside is settled well before this). */
  warmup?: number;
}

/** The four partial derivatives of CLH.close with respect to the next bar's
 *  OHLC, measured from the shipped transform rather than hard-coded. */
export interface ClhSensitivity {
  dO: number;
  dH: number;
  dL: number;
  dC: number;
  /** dH + dC — how far CLH.close moves per unit of price move under the
   *  "price travels there" model. Measured at 0.0279220 on the shipped code. */
  slope: number;
  /** False when dH !== dL or dO !== dC, i.e. the symmetry this solve relies on
   *  no longer holds and the levels must not be trusted. */
  symmetric: boolean;
}

const EPS_REL = 1e-3;

/**
 * Measure how CLH.close responds to the next bar's OHLC, by perturbing a probe
 * bar appended to the real series and running the SHIPPED transform.
 *
 * Also returns the flat-probe result, which is what the last (most recent) bar
 * needs: there is no real "next bar" to recover B(i) from there.
 */
export function calibrateClh(candles: CandleRow[]): {
  sens: ClhSensitivity;
  /** CLH close/open at i = n, for a flat probe bar at the last close. */
  flatNext: { open: number; close: number };
  base: number;
} | null {
  const n = candles.length;
  if (n < 5) return null;
  const base = Number(candles[n - 1].close);
  if (!Number.isFinite(base) || base <= 0) return null;
  const eps = Math.max(Math.abs(base) * EPS_REL, 1e-8);
  const t = Number(candles[n - 1].time) + 1;

  const at = (o: number, h: number, l: number, c: number) => {
    const ext = candles.concat([{ time: t, open: o, high: h, low: l, close: c, volume: 0 } as CandleRow]);
    return transformCandles(ext, 'CLH')[n];
  };

  const f0 = at(base, base, base, base);
  if (!f0 || !Number.isFinite(f0.close) || !Number.isFinite(f0.open)) return null;
  const dO = (at(base + eps, base, base, base).close - f0.close) / eps;
  const dH = (at(base, base + eps, base, base).close - f0.close) / eps;
  const dL = (at(base, base, base + eps, base).close - f0.close) / eps;
  const dC = (at(base, base, base, base + eps).close - f0.close) / eps;

  const tol = 1e-6;
  const symmetric = Math.abs(dH - dL) < tol && Math.abs(dO - dC) < tol;
  return {
    sens: { dO, dH, dL, dC, slope: dH + dC, symmetric },
    flatNext: { open: f0.open, close: f0.close },
    base,
  };
}

/**
 * Levels for every bar, aligned 1:1 with `candles`. Index i holds the levels
 * that govern bar i+1 — see the causality note in the file header.
 *
 * @param candles CLOSED raw candles, ascending. Pass raw bars whatever the chart
 *                is displaying: the band describes the CLH candle, so feeding it
 *                a transformed series would make it describe something else.
 */
export function computeClhBandLevels(candles: CandleRow[], opts: ClhBandOptions = {}): ClhLevelPoint[] {
  const n = candles?.length || 0;
  const out: ClhLevelPoint[] = [];
  if (n < 5) return out;

  const warmup = Math.max(2, Math.floor(opts.warmup ?? 30));
  const cal = calibrateClh(candles);
  const clh = transformCandles(candles, 'CLH');
  if (!cal || !cal.sens.symmetric) {
    // No silent wrong answer: with the symmetry gone the closed form below is
    // not the right one, so emit structure with null levels and let the caller
    // report it. A band that quietly moves to the wrong price is worse than one
    // that is absent.
    for (let i = 0; i < n; i++) {
      out.push({
        time: Number(candles[i].time),
        upper: null, lower: null, flip: null,
        bodyUp: !!(clh[i] && clh[i].close >= clh[i].open),
      });
    }
    return out;
  }

  const { dO, dH, dL, dC, slope } = cal.sens;
  const sumAll = dO + dH + dL + dC;

  for (let i = 0; i < n; i++) {
    const px = Number(candles[i].close);
    const cur = clh[i];
    const bodyUp = !!(cur && cur.close >= cur.open);
    const point: ClhLevelPoint = { time: Number(candles[i].time), upper: null, lower: null, flip: null, bodyUp };

    if (i < warmup || !cur || !Number.isFinite(px) || px <= 0) { out.push(point); continue; }

    // B(i) — the recursion state at bar i, recovered rather than recomputed.
    // For every bar but the last, bar i+1 really happened, so the shipped
    // transform's own output already contains B(i) and we rearrange for it. For
    // the last bar there is no next bar, so the flat probe supplies it.
    let B: number;
    let openNext: number;
    if (i < n - 1) {
      const nx = candles[i + 1];
      const cn = clh[i + 1];
      if (!cn || !Number.isFinite(cn.close)) { out.push(point); continue; }
      B = cn.close - (dO * Number(nx.open) + dH * Number(nx.high) + dL * Number(nx.low) + dC * Number(nx.close));
      openNext = cn.open;
    } else {
      B = cal.flatNext.close - sumAll * cal.base;
      openNext = cal.flatNext.open;
    }

    // With dH === dL, high + low = close[i] + X whichever side X falls, so the
    // two branches collapse into one affine expression and there is no kink at
    // X = close[i].
    const intercept = B + (dO + dH) * px;
    const solve = (target: number): number | null => {
      if (!(Math.abs(slope) > 1e-12) || !Number.isFinite(target)) return null;
      const x = (target - intercept) / slope;
      // A solved price at or below zero is not a price. It means the body cannot
      // be reached from here at all; report absence rather than a nonsense line.
      return Number.isFinite(x) && x > 0 ? x : null;
    };

    const top = Math.max(cur.open, cur.close);
    const bot = Math.min(cur.open, cur.close);
    point.upper = solve(top);
    point.lower = solve(bot);
    point.flip = Number.isFinite(openNext) ? solve(openNext) : null;
    out.push(point);
  }

  return out;
}

/**
 * Carry higher-timeframe levels onto lower-timeframe bars, CAUSALLY.
 *
 * Each LTF bar may see only the HTF bar that had already CLOSED before it. The
 * tempting alternative — the HTF bar CONTAINING this one — is what the chart's
 * context bands draw, and it is look-ahead the moment a decision rests on it:
 * that bar closes in this bar's future. That exact mistake made a gated entry
 * measure +1.48% of pure hindsight three days ago.
 *
 * @param htfLevels levels computed from CLOSED HTF candles (drop the forming one)
 * @param htfTimes  the HTF bar times those levels came from, ascending
 * @param htfMinutes HTF bar size, to know when a bar has closed
 */
export function stampHtfLevels(
  ltfTimes: number[],
  htfLevels: ClhLevelPoint[],
  htfMinutes: number,
): Array<ClhLevelPoint | null> {
  const out: Array<ClhLevelPoint | null> = new Array(ltfTimes.length).fill(null);
  if (!htfLevels.length || !(htfMinutes > 0)) return out;
  const durSec = htfMinutes * 60;
  let k = -1;
  for (let i = 0; i < ltfTimes.length; i++) {
    const t = ltfTimes[i];
    // Advance only past HTF bars that have FULLY closed before this bar.
    while (k + 1 < htfLevels.length && htfLevels[k + 1].time + durSec <= t) k++;
    if (k >= 0) out[i] = htfLevels[k];
  }
  return out;
}

/**
 * Direction of a STEPPED level series, for colouring a line by its OWN slope.
 *
 * The midline is a staircase: it holds flat for a bar and then jumps. A flat
 * step is NOT a change of direction, so it carries the previous one rather than
 * resetting to neutral - otherwise a long hold between two falls would paint as
 * a third colour nobody asked for. The leading run has nothing earlier to
 * compare against and is back-filled with the first direction the series
 * actually takes, so the line never opens in a colour it has not earned.
 *
 * Returns true where the level ROSE, false where it FELL, null only when the
 * series never moves at all.
 */
export function stepDirections(values: Array<number | null>): Array<boolean | null> {
  const out: Array<boolean | null> = new Array(values.length).fill(null);
  let last: boolean | null = null;
  let prev: number | null = null;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v == null || !Number.isFinite(v)) { out[i] = last; continue; }
    if (prev != null) {
      if (v > prev) last = true;
      else if (v < prev) last = false;
    }
    out[i] = last;
    prev = v;
  }
  let first: boolean | null = null;
  for (let k = 0; k < out.length; k++) { if (out[k] != null) { first = out[k]; break; } }
  if (first != null) for (let i = 0; i < out.length && out[i] == null; i++) out[i] = first;
  return out;
}
